import { ReactComponent as RecordIcon } from '@/components/assets/icons/RecordIcon.svg';
import { ReactComponent as StopIcon } from '@/components/assets/icons/RecordStopIcon.svg';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import ModalBody from '@/components/Modal/ModalBody';
import ModalFooter from '@/components/Modal/ModalFooter';
import ModalHeader from '@/components/Modal/ModalHeader';
import { Grey, Primary, Secondary, White } from '@/styles/Colors';
import { FontSize, FontWeight } from '@/styles/Typography';
import { audioBufferToWav, getAudioBuffer } from '@/util/audio';
import classNames from 'classnames';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const recordStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: Secondary[300],
  borderColor: Secondary[300],
  borderRadius: '0.25rem',
  fontSize: FontSize['Lg'],
  lineHeight: '150%',
  padding: '0.25rem 0.5rem',
  cursor: 'pointer',
  color: Grey[50],
  fontWeight: FontWeight['SemiBold'],
  minWidth: '5rem',
  justifyContent: 'space-around',
};
const simpleRecordStyle = {
  ...recordStyle,
  padding: '0.175rem 0.25rem',
  minWidth: '1.5rem',
};
const nameLabelStyle = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: FontSize['Md'],
  color: White,
};
const nameInputStyle = {
  boxSizing: 'border-box' as const,
  width: '100%',
  minWidth: '16rem',
  padding: '0.25rem 0.5rem',
  borderRadius: '0.25rem',
  border: `1px solid ${Grey[300]}`,
  backgroundColor: Grey[700],
  color: White,
  fontSize: FontSize['Md'],
};

export interface Recorder {
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
}
export const recordAudio = () =>
  new Promise<Recorder>((resolve, reject) => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          /**
           * [workaround]
           * 주변 소음으로 인한 Input Volume이 자동 조절 되지 않게 속성 세팅(G-star 2023 에서 헤드셋을 통한 환경 테스트 완료)
           */
          audio: {
            autoGainControl: false,
            noiseSuppression: false,
          },
        });

        if (!MediaRecorder || typeof MediaRecorder !== 'function') {
          reject(new Error('MediaRecorder is not supported in this browser.'));
          return;
        }

        const mediaRecorder = new MediaRecorder(stream);
        const audioChunks: BlobPart[] = [];

        mediaRecorder.addEventListener('dataavailable', (event) => {
          audioChunks.push(event.data);
        });

        const start = () =>
          new Promise<void>((resolve) => {
            mediaRecorder.addEventListener('start', () => {
              resolve();
            });
            mediaRecorder.start();
          });

        const stop = () =>
          new Promise<Blob>((resolve) => {
            mediaRecorder.addEventListener('stop', async () => {
              resolve(new Blob(audioChunks));
            });

            mediaRecorder.stop();
          });

        resolve({ start, stop });
      } catch (error) {
        reject(
          new Error(
            'Error accessing the microphone: ' + (error as Error).message
          )
        );
      }
    })();
  });

const getDigitNumber = (num: number) => {
  return num >= 10 ? num : '0' + num;
};

const getFileDate = () => {
  let now = new Date();
  let year = now.getFullYear();
  let month = getDigitNumber(now.getMonth() + 1);
  let date = getDigitNumber(now.getDate());
  let hour = getDigitNumber(now.getHours());
  let minute = getDigitNumber(now.getMinutes());
  let second = getDigitNumber(now.getSeconds());
  let apm = now.getHours() >= 12 ? 'PM' : 'AM';
  return `${year}-${month}-${date} ${hour}:${minute}:${second} ${apm}`;
};

interface RecordProps {
  addFiles: (files: File[]) => void;
  captions?: string[];
  defaultName?: string;
  className?: string;
}

/** A finished recording waiting to be named. Its presence opens the dialog. */
interface PendingRecord {
  blob: Blob;
  generatedName: string;
}

const Record: React.FC<RecordProps> = ({
  addFiles,
  captions,
  className,
  defaultName = 'Recorded',
}) => {
  const { t } = useTranslation();
  const id = useId();
  const titleId = `${id}-title`;
  const nameFieldId = `${id}-name`;
  const [isRecording, setRecording] = React.useState<boolean | undefined>();
  const [recorder, setRecorder] = React.useState<Recorder | undefined>();
  const [pendingRecord, setPendingRecord] = React.useState<
    PendingRecord | undefined
  >();
  const [fileName, setFileName] = React.useState('');
  const nameFieldRef = useRef<HTMLInputElement>(null);
  const onClick = useCallback(() => {
    setRecording((pre) => !pre);
  }, [setRecording]);

  useEffect(() => {
    if (isRecording && !recorder) {
      recordAudio().then((recorder) => {
        setRecorder(recorder);
        recorder.start();
      });
    } else if (isRecording === false) {
      recorder?.stop().then((blob) => {
        // ISSUE-056: the recording is held here and named in-app, instead of
        // through a blocking native prompt dialog.
        const generatedName = `${defaultName}_${getFileDate()}`;
        setPendingRecord({ blob, generatedName });
        setFileName(generatedName);
        setRecorder(undefined);
      });
    }
  }, [isRecording, recorder, defaultName]);

  // Put focus in the field the user opened this dialog to type in. A parent's
  // effects flush AFTER its children's, so this runs after Modal's own
  // open-focus effect — it places focus INSIDE the trap ISSUE-055 installed
  // rather than competing with it, and Modal has already captured the record
  // button as its restore target by the time this runs.
  useEffect(() => {
    if (!pendingRecord) return;
    nameFieldRef.current?.focus();
    nameFieldRef.current?.select();
  }, [pendingRecord]);

  const finishNaming = useCallback(
    async (name: string) => {
      if (!pendingRecord) return;
      const { blob, generatedName } = pendingRecord;
      setPendingRecord(undefined);
      const audioBuffer = await getAudioBuffer(
        await new Response(blob).arrayBuffer()
      );
      addFiles([
        new File(
          [audioBufferToWav(audioBuffer as AudioBuffer)],
          `${name.trim() || generatedName}.wav`,
          { type: 'audio/wav' }
        ),
      ]);
    },
    [pendingRecord, addFiles]
  );

  // Dismissing means "do not rename", never "discard": a recording cannot be
  // re-made, and the native prompt this replaces kept it too
  // (`?? recordFileName`).
  const cancelNaming = () => {
    void finishNaming('');
  };

  return (
    <>
      <Button
        style={captions ? recordStyle : simpleRecordStyle}
        onClick={onClick}
        startIcon={captions && (isRecording ? <StopIcon /> : <RecordIcon />)}
        className={classNames('file-record-button', className)}
      >
        {/* {todo 디자인 최종 확인 필요} */}
        {captions ? isRecording ? captions[1] : captions[0] : <RecordIcon />}
      </Button>
      <Modal
        isOpen={!!pendingRecord}
        onClose={cancelNaming}
        className="record-name-modal"
        aria-labelledby={titleId}
      >
        <ModalHeader id={titleId}>{t('Name your recording')}</ModalHeader>
        <ModalBody>
          <label htmlFor={nameFieldId} style={nameLabelStyle}>
            {t('File name')}
          </label>
          <input
            id={nameFieldId}
            ref={nameFieldRef}
            type="text"
            value={fileName}
            style={nameInputStyle}
            onChange={(event) => setFileName(event.target.value)}
            onKeyDown={(event) => {
              // Parity with the native prompt this replaces: Enter commits.
              // NOT the Enter that accepts an IME candidate, though — a
              // Korean/Japanese/Chinese composition fires keydown with
              // `isComposing` true for the key that COMMITS the candidate, and
              // saving there would name the file with a half-typed string. The
              // native prompt never had this defect because the browser owned
              // the field; an in-app field has to guard it.
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                void finishNaming(fileName);
              }
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color={White} variant="outlined" onClick={cancelNaming}>
            {t('Cancel')}
          </Button>
          <Button
            color={Primary[400]}
            onClick={() => void finishNaming(fileName)}
          >
            {t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
export default Record;
